import CreateAxiosInstance from './axiosConfig';
const APIClient = CreateAxiosInstance(import.meta.env.VITE_API_BASE_URL);

export async function getSchoolRecords({ searchQuery, page, perPage, sortBy, sortOrder }) {
  return await APIClient.get('/school-records/', {
    params: {
      search_query: searchQuery,
      page,
      per_page: perPage,
      sort_by: sortBy,
      sort_order: sortOrder,
    },
  });
}

// country/state/stateCode come from the client's cached /geo/me answer. Passing them
// lets the endpoint skip its own IP lookup, which would otherwise run on every
// debounced keystroke. Spread last so they win over a stale `location`.
export async function searchSchools({
  name,
  page,
  pageSize,
  location = {},
  country,
  state,
  stateCode,
  districtsOnly = false,
  filterState,
}) {
  return await APIClient.get('/school-records/search', {
    params: {
      name_query: name,
      page,
      per_page: pageSize,
      // districts_only is a distinct filter from exclude_districts; when the caller
      // wants districts only we send districts_only=true and stop excluding them.
      exclude_districts: !districtsOnly,
      districts_only: districtsOnly,
      // Hard state filter (matches full name OR 2-letter code); distinct from the
      // boost-only state/stateCode geo params below, so the geo spread can't clobber it.
      filter_state: filterState,
      ...location,
      country,
      state,
      state_code: stateCode,
    },
  });
}

export async function getSchoolRecordsByLocation({
  city,
  state,
  country,
  lat,
  lon,
  location,
  query,
  exclude_districts,
}) {
  return await APIClient.get(`/school-records/get_schools_in_city`, {
    params: {
      city,
      state,
      country,
      lat,
      lon,
      location,
      query,
      exclude_districts,
    },
    defaultErrorMessage: 'Error getting school records by location',
  });
}

export async function connectUserToExistingSchoolRecord({ school_id }) {
  return await APIClient.patch(`/school-records/connect`, {
    school_id,
  });
}

export async function createSchoolRecordAndConnect({ name, city, state, country, is_district }) {
  return await APIClient.post(`/school-records/connect`, {
    name,
    city,
    state,
    country,
    is_district: is_district,
  });
}

export async function processSchoolRecordsURL({ spreadsheet_url }) {
  return await APIClient.patch('/school-records/bulk_update', { spreadsheet_url }, { timeout: 0 });
}

export async function addSchoolRecord({ name, city, state, country, enrollment, is_district }) {
  return await APIClient.post(
    `/school-records/`,
    {
      name,
      city,
      state,
      country,
      enrollment,
      is_district,
    },
    {
      defaultErrorMessage: 'Error adding school record',
    }
  );
}

export async function editSchoolRecord({
  school_record_id,
  salesforce_id,
  name,
  city,
  state,
  country,
  enrollment,
  is_district,
}) {
  return await APIClient.patch(
    `/school-records/`,
    {
      school_record_id,
      salesforce_id,
      name,
      city,
      state,
      country,
      enrollment,
      is_district,
    },
    {
      defaultErrorMessage: 'Error updating school record',
    }
  );
}

export async function getSchoolRecordBySalesforceId({ salesforceId }) {
  return await APIClient.get(`/school-records/salesforce_id?salesforce_id=${salesforceId}`, {
    // Errors handled in the component
    customErrorHandler: () => {},
  });
}

export async function mergeSchoolRecords({ primarySchoolId, otherSchoolIds }) {
  return await APIClient.post(`/school-records/merge`, {
    primary_school_id: primarySchoolId,
    other_school_ids: otherSchoolIds,
  });
}

export async function deleteUserSchoolRecord({ schoolRecordId }) {
  const data = { school_id: schoolRecordId };
  return await APIClient.delete(`/school-records/connect`, {
    data: data,
  });
}
